import type { ClimberPose } from '@shared/types';

type ClimberColor = 'red' | 'blue';

interface ClimberProps {
  color?: ClimberColor;
  pose?: ClimberPose;
  scale?: number;
  mirror?: boolean;
}

/** SVG fallback climber — used in DOM contexts (avatars, result screen). */
export function Climber({ color = 'red', pose = 'idle', scale = 1, mirror = false }: ClimberProps) {
  const body = color === 'red' ? '#E63946' : '#3B82F6';
  const bodyLt = color === 'red' ? '#ff6671' : '#6ba2ff';
  const skin = '#f7d3b3';
  const skinSh = '#e0b794';
  const dark = '#1a1234';
  const cap = color === 'red' ? '#c1232f' : '#1d4ed8';

  const armUp = pose === 'climb' || pose === 'victory';
  const fall = pose === 'fall';
  const victory = pose === 'victory';

  return (
    <div
      style={{
        width: 36 * scale,
        height: 52 * scale,
        position: 'relative',
        transform: `${mirror ? 'scaleX(-1)' : ''} ${fall ? 'rotate(-15deg)' : ''}`,
        transformOrigin: 'center bottom',
      }}
    >
      <svg viewBox="0 0 36 52" width={36 * scale} height={52 * scale} style={{ overflow: 'visible' }}>
        <ellipse cx="18" cy="51" rx="11" ry="2" fill="rgba(0,0,0,0.25)" />
        <rect x="11" y="32" width="5" height="14" rx="2.5" fill={body} />
        <rect
          x="20"
          y="32"
          width="5"
          height={fall ? 12 : 16}
          rx="2.5"
          fill={body}
          transform={fall ? 'rotate(20 22 32)' : 'rotate(-8 22 32)'}
        />
        <rect x="10" y="44" width="7" height="4" rx="1.5" fill={dark} />
        <rect
          x="19"
          y={fall ? 42 : 46}
          width="7"
          height="4"
          rx="1.5"
          fill={dark}
          transform={fall ? 'rotate(20 22 44)' : 'rotate(-8 22 46)'}
        />
        <rect x="8" y="20" width="20" height="16" rx="5" fill={body} />
        <rect x="10" y="21" width="6" height="13" rx="3" fill={bodyLt} opacity="0.6" />
        <circle cx="18" cy="27" r="3.5" fill="rgba(255,255,255,0.95)" />
        <text
          x="18"
          y="29"
          textAnchor="middle"
          fontSize="5"
          fontWeight="900"
          fill={dark}
          fontFamily="Bungee, Impact"
        >
          {color === 'red' ? '1' : '2'}
        </text>
        {armUp ? (
          <>
            <rect
              x="5"
              y={victory ? 4 : 10}
              width="4.5"
              height="14"
              rx="2.2"
              fill={body}
              transform={`rotate(${victory ? -10 : 15} 7 20)`}
            />
            <rect
              x="26.5"
              y={victory ? 4 : 10}
              width="4.5"
              height="14"
              rx="2.2"
              fill={body}
              transform={`rotate(${victory ? 10 : -15} 28 20)`}
            />
            <circle cx={victory ? 5.5 : 7} cy={victory ? 5 : 11} r="2.5" fill={skin} />
            <circle cx={victory ? 30.5 : 29} cy={victory ? 5 : 11} r="2.5" fill={skin} />
          </>
        ) : (
          <>
            <rect
              x="4.5"
              y="22"
              width="4.5"
              height="14"
              rx="2.2"
              fill={body}
              transform={`rotate(${fall ? -45 : 15} 7 28)`}
            />
            <rect
              x="27"
              y="22"
              width="4.5"
              height="14"
              rx="2.2"
              fill={body}
              transform={`rotate(${fall ? 45 : -15} 29 28)`}
            />
            <circle cx={fall ? 2 : 6} cy={fall ? 24 : 36} r="2.3" fill={skin} />
            <circle cx={fall ? 34 : 30} cy={fall ? 24 : 36} r="2.3" fill={skin} />
          </>
        )}
        <circle cx="18" cy="14" r="8" fill={skin} />
        <ellipse cx="20" cy="16" rx="3" ry="4" fill={skinSh} opacity="0.4" />
        {color === 'red' ? (
          <>
            <path d="M 10 12 Q 10 5 18 5 Q 26 5 26 12 L 26 14 L 10 14 Z" fill={cap} />
            <rect x="22" y="13" width="9" height="3" rx="1" fill={cap} />
            <circle cx="18" cy="9" r="1" fill="white" />
          </>
        ) : (
          <>
            <path d="M 9 13 Q 9 4 18 4 Q 27 4 27 13 L 27 15 L 9 15 Z" fill={cap} />
            <rect x="10" y="13" width="16" height="2.5" fill={cap} stroke="white" strokeWidth="0.5" />
          </>
        )}
        {victory ? (
          <>
            <path d="M 14 14 q 1.5 -2 3 0" stroke={dark} strokeWidth="1.2" fill="none" strokeLinecap="round" />
            <path d="M 19 14 q 1.5 -2 3 0" stroke={dark} strokeWidth="1.2" fill="none" strokeLinecap="round" />
            <path d="M 15 17 q 3 3 6 0" stroke={dark} strokeWidth="1.2" fill="none" strokeLinecap="round" />
          </>
        ) : fall ? (
          <>
            <circle cx="15" cy="14" r="1.2" fill={dark} />
            <circle cx="21" cy="14" r="1.2" fill={dark} />
            <ellipse cx="18" cy="17.5" rx="1.4" ry="1.8" fill={dark} />
          </>
        ) : (
          <>
            <rect x="14" y="13.5" width="2" height="1.5" rx="0.6" fill={dark} />
            <rect x="20" y="13.5" width="2" height="1.5" rx="0.6" fill={dark} />
            <path d="M 15 17 q 3 1.5 6 0" stroke={dark} strokeWidth="1" fill="none" strokeLinecap="round" />
          </>
        )}
        {victory && (
          <g>
            <circle cx="2" cy="2" r="1.5" fill="#F4C724" />
            <circle cx="34" cy="3" r="1" fill="#F4C724" />
            <circle cx="6" cy="-2" r="0.8" fill="#F4C724" />
            <circle cx="30" cy="-3" r="1.2" fill="#F4C724" />
          </g>
        )}
      </svg>
    </div>
  );
}

